// Copyright (c) 2024 by Audere

import { PayloadAction, createSelector, createSlice } from "@reduxjs/toolkit";

import { RootState } from "../store";
import { LabelingRole } from "../api/labelApiSlice";

export type PatientGroupRoles = Record<string, LabelingRole[]>;
export type PersistentGroupRole = Record<string, GroupRole>;
export interface GroupRole {
  group: string;
  role: LabelingRole;
}

export interface State {
  email: string;
  name: string;
  picture: string | null;
  credentials: string;
  token: string;
  expires: string;
  patientGroupRoles?: PatientGroupRoles;
  persistentGroupRole?: PersistentGroupRole;
}

const authSlice = createSlice({
  name: "auth",
  initialState: {
    email: "",
    name: "",
    picture: null,
    credentials: "",
    token: "",
    expires: "",
    persistentGroupRole: undefined,
    patientGroupRoles: undefined,
  },
  reducers: {
    setCredentials: (state: State, action: PayloadAction<State>) => {
      state.email = action.payload.email;
      state.name = action.payload.name;
      state.picture = action.payload.picture;
      state.credentials = action.payload.credentials;
      state.token = action.payload.token;
      state.expires = action.payload.expires;
      state.patientGroupRoles = action.payload.patientGroupRoles;
      state.persistentGroupRole = action.payload.persistentGroupRole;
    },
    setPersistentGroupRole: (
      state: State,
      action: PayloadAction<GroupRole>
    ) => {
      state.persistentGroupRole = Object.assign(
        state.persistentGroupRole ?? {},
        {
          [state.email]: action.payload,
        }
      );
    },
    setPatientGroupRoles: (
      state: State,
      action: PayloadAction<PatientGroupRoles>
    ) => {
      state.patientGroupRoles = action.payload;
    },
    logOut: (state: State) => {
      state.email = "";
      state.name = "";
      state.picture = null;
      state.credentials = "";
      state.token = "";
      state.expires = "";
      state.patientGroupRoles = undefined;
    },
  },
});

export const {
  setCredentials,
  setPersistentGroupRole,
  setPatientGroupRoles,
  logOut,
} = authSlice.actions;

export default authSlice.reducer;

export const selectCurrentEmail = (state: RootState) => state.auth.email;
export const selectCurrentName = (state: RootState) => state.auth.name;
export const selectCurrentPicture = (state: RootState): string | null =>
  state.auth.picture;
export const selectCurrentToken = (state: RootState) => state.auth.token;
export const selectCurrentPatientGroupRoles = (
  state: RootState
): PatientGroupRoles | undefined => state.auth.patientGroupRoles;
export const selectCurrentPersistentGroupRole = (
  state: RootState
): PersistentGroupRole | undefined => state.auth.persistentGroupRole;
export const selectCurrentUserPersistentGroupRole = createSelector(
  selectCurrentEmail,
  selectCurrentPersistentGroupRole,
  (email, persistentGroupRole): GroupRole =>
    persistentGroupRole?.[email] ?? {
      group: "unknown",
      role: LabelingRole.default,
    }
);
